import React, { useMemo } from 'react';
import type { FxType } from '../services/confirmationDesignSettings';

interface ConfirmationFxProps {
  type: FxType;
  color: string;
}

/**
 * Composant qui gère les effets visuels overlay
 * (confettis, particules, sparkles, glow).
 * Rendu absolu, doit être à l'intérieur d'un parent en `relative`.
 */
export const ConfirmationFx: React.FC<ConfirmationFxProps> = ({ type, color }) => {
  // Génère des positions aléatoires stables (memo)
  const particles = useMemo(() => {
    const count = type === 'confetti' ? 30 : type === 'particles' ? 20 : type === 'sparkle' ? 12 : 0;
    return Array.from({ length: count }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      top: Math.random() * 100,
      delay: Math.random() * 3,
      duration: 2 + Math.random() * 2,
      size: 4 + Math.random() * 8,
    }));
  }, [type]);

  if (type === 'none') return null;

  if (type === 'glow') {
    // Pas de particules, juste un halo ; le parent gère via classe CSS
    return null;
  }

  if (type === 'confetti') {
    const colors = ['#f97316', '#dc2626', '#fbbf24', '#10b981', '#3b82f6', '#a855f7'];
    return (
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {particles.map((p) => (
          <div
            key={p.id}
            className="absolute confirmation-confetti"
            style={{
              left: `${p.left}%`,
              top: `-${p.size}px`,
              width: `${p.size}px`,
              height: `${p.size * 0.5}px`,
              backgroundColor: colors[p.id % colors.length],
              animationDelay: `${p.delay}s`,
              animationDuration: `${p.duration}s`,
              borderRadius: '2px',
            }}
          />
        ))}
      </div>
    );
  }

  if (type === 'particles') {
    return (
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {particles.map((p) => (
          <div
            key={p.id}
            className="absolute rounded-full confirmation-particle"
            style={{
              left: `${p.left}%`,
              top: `${p.top}%`,
              width: `${p.size}px`,
              height: `${p.size}px`,
              backgroundColor: color,
              animationDelay: `${p.delay}s`,
              animationDuration: `${p.duration}s`,
            }}
          />
        ))}
      </div>
    );
  }

  if (type === 'sparkle') {
    return (
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {particles.map((p) => (
          <div
            key={p.id}
            className="absolute confirmation-sparkle"
            style={{
              left: `${p.left}%`,
              top: `${p.top}%`,
              width: `${p.size + 4}px`,
              height: `${p.size + 4}px`,
              animationDelay: `${p.delay}s`,
              animationDuration: `${p.duration}s`,
            }}
          >
            <svg viewBox="0 0 24 24" fill={color}>
              <path d="M12 0l3 9 9 3-9 3-3 9-3-9-9-3 9-3z" />
            </svg>
          </div>
        ))}
      </div>
    );
  }

  return null;
};

export default ConfirmationFx;
